import { useState } from 'react';
import { FolderIcon, FolderOpenIcon, ChevronRightIcon, ChevronDownIcon, VideoCameraIcon } from '@heroicons/react/24/outline';

/**
 * Read-only tree of camera groups with their cameras nested underneath — shared by both
 * dashboards' "tree" view. Presentational: the host page passes already-fetched groups
 * (nested, with `children`) and the flat camera list (each camera carries `groups: [{id}]`).
 */
interface CameraTreeProps {
  groups: any[];
  cameras: any[];
  /** Optional: called when a camera node is clicked (host navigates to that camera's view). */
  onCameraClick?: (camera: any) => void;
}

export default function CameraTree({ groups, cameras, onCameraClick }: CameraTreeProps) {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const toggleGroup = (id: string) =>
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  function renderGroup(group: any, level = 0) {
    const expanded = expandedGroups.has(group.id);
    const hasChildren = group.children?.length > 0;
    const groupCameras = cameras.filter((c) => c.groups?.some((g: any) => g.id === group.id));

    return (
      <div key={group.id}>
        <div
          onClick={() => toggleGroup(group.id)}
          className="flex items-center gap-2 py-2 px-2 hover:bg-muted rounded-lg cursor-pointer transition-colors"
          style={{ paddingLeft: `${level * 16 + 8}px` }}
        >
          <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
            {hasChildren || groupCameras.length > 0 ? (
              expanded ? <ChevronDownIcon className="w-4 h-4 text-muted-foreground" /> : <ChevronRightIcon className="w-4 h-4 text-muted-foreground" />
            ) : (
              <div className="w-4 h-4" />
            )}
          </span>
          {expanded ? <FolderOpenIcon className="h-4 w-4 text-yellow-400 flex-shrink-0" /> : <FolderIcon className="h-4 w-4 text-yellow-400 flex-shrink-0" />}
          <span className="text-[13px] font-medium text-foreground truncate flex-1">{group.name}</span>
          {groupCameras.length > 0 && (
            <span className="text-[11px] text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{groupCameras.length}</span>
          )}
        </div>
        {expanded && (
          <>
            {group.children?.map((child: any) => renderGroup(child, level + 1))}
            {groupCameras.map((cam) => (
              <div
                key={cam.id || cam.camera_id}
                onClick={onCameraClick ? () => onCameraClick(cam) : undefined}
                className={`flex items-center gap-2 py-1.5 px-2 hover:bg-muted/50 rounded-lg transition-colors ${onCameraClick ? 'cursor-pointer' : ''}`}
                style={{ paddingLeft: `${(level + 1) * 16 + 28}px` }}
              >
                <VideoCameraIcon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <span className="text-[13px] text-foreground truncate">{cam.name}</span>
                <span className={`ml-auto h-2 w-2 rounded-full ${cam.status === 'active' || cam.status === 'streaming' || cam.status === 'online' ? 'bg-emerald-400' : 'bg-muted-foreground'}`} />
              </div>
            ))}
          </>
        )}
      </div>
    );
  }

  if (!groups || groups.length === 0) {
    return (
      <div className="py-16 text-center text-muted-foreground">
        <p className="text-[15px] font-medium">No groups created yet</p>
      </div>
    );
  }
  return <div className="rounded-xl border border-border bg-card p-4 shadow-card space-y-0.5">{groups.map((g) => renderGroup(g))}</div>;
}
